import {
  Role,
  Service,
  Council,
  StudyLevelConfiguration,
  Fine,
  FineRule,
  UserCertificateApproved,
} from "@/types";
import { ApprovalEntry } from "@/types/educationbackground.type";
import {
  Question,
  ExamSchedule,
  ExamResultDTO,
  CouncilExamConfigDTO,
  PracticalExamResultDTO,
} from "@/types/examination.types";
import { InternshipSiteDTO } from "@/types/internship-sites.types";
import { atom } from "recoil";

export const systemRoles = atom<Role[]>({
  key: "systemRoles",
  default: [] as Role[],
});

export const showCreateRoleState = atom({
  key: "showCreateRoleState",
  default: false,
});
export const useSideBarFilter = atom<{
  useFilter: boolean;
  filterParam: string;
} | null>({
  key: "useSideBarFilter",
  default: null,
});

export const showApproveRequestState = atom<{
  show: boolean;
  requestId: string;
  action: "approve" | "reject";
} | null>({
  key: "showApproveRequestState",
  default: null,
});

export const showAddUserState = atom({
  key: "showAddUserState",
  default: false,
});

export const showDeleteRoleState = atom<{
  show: boolean;
  roleId: string;
} | null>({
  key: "showDeleteRoleState",
  default: null,
});

export const showDeleteExitAwardState = atom<{
  show: boolean;
  exitAwardId: string;
} | null>({
  key: "showDeleteExitAwardState",
  default: null,
});

export const showDeleteAcademicDegreeProgramState = atom<{
  show: boolean;
  AcademicDegreeProgramId: string;
} | null>({
  key: "showDeleteAcademicDegreeProgramState",
  default: null,
});

export const showDeleteProfessionCategoryState = atom<{
  show: boolean;
  ProfessionCategoryId: string;
} | null>({
  key: "showDeleteProfessionCategoryState",
  default: null,
});

export const showDeleteProfessionCredentialState = atom<{
  show: boolean;
  ProfessionCredentialId: string;
} | null>({
  key: "showDeleteProfessionCredentialState",
  default: null,
});


export const showDeleteBankState = atom<{
  show: boolean;
  BankId: string;
} | null>({
  key: "showDeleteBankState",
  default: null,
});

export const showDeleteIremboConfigState = atom<{
  show: boolean;
  councilId: string;
} | null>({
  key: "showDeleteIremboConfigState",
  default: null,
});

export const showAddOrEditIremboConfigState = atom<{
  show: boolean;
  action: "add" | "edit";
  councilId?: string;
} | null>({
  key: "showAddOrEditIremboConfigState",
  default: null,
});

export const showVerifyIremboConfigState = atom<{
  show: boolean;
  councilId: string;
} | null>({
  key: "showVerifyIremboConfigState",
  default: null,
});

// SMTP configuration modals
export const showAddOrEditSmtpConfigState = atom<{
  show: boolean;
  action: "add" | "edit";
  councilId?: string;
  config?: any;
} | null>({
  key: "showAddOrEditSmtpConfigState",
  default: null,
});

export const showVerifySmtpConfigState = atom<{
  show: boolean;
  councilId: string;
} | null>({
  key: "showVerifySmtpConfigState",
  default: null,
});

export const showTestSmtpConfigState = atom<{
  show: boolean;
  councilId: string;
  councilName?: string;
} | null>({
  key: "showTestSmtpConfigState",
  default: null,
});

export const showDeleteServiceState = atom<{
  show: boolean;
  service: Service;
} | null>({
  key: "showDeleteServiceState",
  default: null,
});

export const showEditRoleState = atom<{
  show: boolean;
  roleId: string;
} | null>({
  key: "showEditRoleState",
  default: null,
});

export const showManageUserState = atom<{
  show: boolean;
  userId: string;
} | null>({
  key: "showManageUserState",
  default: null,
});

export const showAssignRegNoState = atom<{
  show: boolean;
  userId: string;
} | null>({
  key: "showAssignRegNoState",
  default: null,
});

export const showAssignIndexState = atom<{
  show: boolean;
  userId: string;
} | null>({
  key: "showAssignIndexState",
  default: null,
});

export const showAddOrEditCouncilState = atom<{
  show: boolean;
  action: "add" | "edit";
  council?: Council;
} | null>({
  key: "showAddOrEditCouncilState",
  default: null,
});

export const showDeleteCouncilState = atom<{
  show: boolean;
  council: Council;
} | null>({
  key: "showDeleteCouncilState",
  default: null,
});

export const showAddOrEditApprovalStructureState = atom<{
  show: boolean;
  action: "add" | "edit";
  approvalStructure?: ApprovalEntry;
} | null>({
  key: "showAddOrEditApprovalStructureState",
  default: null,
});

export const showDeleteApprovalStructureState = atom<{
  show: boolean;
  approvalStructure?: ApprovalEntry;
} | null>({
  key: "showDeleteApprovalStructureState",
  default: null,
});

export const showDeleteStudyLevelState = atom<{
  show: boolean;
  studyLevel: StudyLevelConfiguration;
} | null>({
  key: "showDeleteStudyLevelState",
  default: null,
});

export const showAddOrEditStudyLevelState = atom<{
  show: boolean;
  action: "add" | "edit";
  studyLevel?: StudyLevelConfiguration;
} | null>({
  key: "showAddOrEditStudyLevelState",
  default: null,
});

export const showDeleteFineState = atom<{
  show: boolean;
  fine: Fine;
} | null>({
  key: "showDeleteFineState",
  default: null,
});

export const showAddOrEditFineState = atom<{
  show: boolean;
  action: "add" | "edit";
  fine?: Fine;
} | null>({
  key: "showAddOrEditFineState",
  default: null,
});

export const showAddOrUpdateServiceState = atom<{
  show: boolean;
  action: "add" | "edit";
  service?: Service;
} | null>({
  key: "showAddOrUpdateServiceState",
  default: null,
});

export const showDeleteQuestionState = atom<{
  show: boolean;
  question: Question;
} | null>({
  key: "showDeleteQuestionState",
  default: null,
});

export const showEditQuestionState = atom<{
  show: boolean;
  question: Question;
} | null>({
  key: "showEditQuestionState",
  default: null,
});

export const showDeleteExamScheduleState = atom<{
  show: boolean;
  examSchedule: ExamSchedule;
} | null>({
  key: "showDeleteExamScheduleState",
  default: null,
});

export const showScheduleExamModalState = atom<{
  show: boolean;
  application: UserCertificateApproved;
} | null>({
  key: "showScheduleExamModalState",
  default: null,
});

export const showSubmitAppealState = atom<{
  show: boolean;
  result?: ExamResultDTO; // For theory appeals
  practicalResult?: PracticalExamResultDTO; // For practical appeals
} | null>({
  key: "showSubmitAppealState",
  default: null,
});

export const showInitiateRetakeState = atom<boolean>({
  key: "showInitiateRetakeState",
  default: false,
});

export const showQuestionAuditState = atom<{
  show: boolean;
  question: Question;
} | null>({
  key: "showQuestionAuditState",
  default: null,
});

export const showAddOrEditExamConfigState = atom<{
  show: boolean;
  action: "add" | "edit";
  config?: CouncilExamConfigDTO;
} | null>({
  key: "showAddOrEditExamConfigState",
  default: null,
});

export const showAddOrEditFineRuleState = atom<{
  show: boolean;
  action: "add" | "edit";
  fineRule?: FineRule;
} | null>({
  key: "showAddOrEditFineRuleState",
  default: null,
});

// User Merge Modal States
export const showPreviewMergeState = atom<{
  show: boolean;
  winnerId: string;
  duplicateId: string;
  nationalId?: string;
} | null>({
  key: "showPreviewMergeState",
  default: null,
});

export const showConfirmMergeState = atom<{
  show: boolean;
  winnerId: string;
  duplicateId: string;
  winnerName?: string;
  winnerEmail?: string;
  duplicateName?: string;
  duplicateEmail?: string;
} | null>({
  key: "showConfirmMergeState",
  default: null,
});

export const showAutoMergeState = atom<boolean>({
  key: "showAutoMergeState",
  default: false,
});

// User Merge v2.0.0 - Manual Selection State
export const showManualSelectionState = atom<{
  show: boolean;
  winnerId: string;
  duplicateId: string;
  winnerName?: string;
  winnerEmail?: string;
  duplicateName?: string;
  duplicateEmail?: string;
} | null>({
  key: "showManualSelectionState",
  default: null,
});

export const mergeRecordSelectionsState = atom<Record<string, string[]>>({
  key: "mergeRecordSelectionsState",
  default: {},
});

export const mergeReasonState = atom<string>({
  key: "mergeReasonState",
  default: "",
});

export const showAddOrUpdateInternshipSiteState = atom<{
  show: boolean;
  action: "add" | "edit";
  internshipSite?: InternshipSiteDTO;
} | null>({
  key: "showAddOrUpdateInternshipSiteState",
  default: null,
});

export const showUpdateUserState = atom<{
  show: boolean;
  userId: string;
} | null>({
  key: "showUpdateUserState",
  default: null,
});
